/* eslint-disable react-hooks/exhaustive-deps */
import {
  marketingOverviewFilter,
  ICampaignResponse,
  CampaignType,
} from '@dataTypes/marketing.type'
import { useAppSelector } from '@store/hooks'
import { isDateInRange } from '@utils/date.utils'
import { format } from 'date-fns'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface ICampaignType {
  x: string
  Promotion: number
  Recall: number
  Reminder: number
  Celebration: number
  'Pat. edu': number
  'Follow-up': number
  'Post-Tx': number
}

interface CampaignTypeHooks {
  campaigns: ICampaignResponse[]
  campaignCategories: string[]
  campaignDropdownOptions: string[]
  data: ICampaignType[]
  selectedCampaign: string
  setSelectedCampaign: Dispatch<SetStateAction<string>>
}

const campaignCategories = [
  'Promotion',
  'Recall',
  'Reminder',
  'Celebration',
  'Pat. edu',
  'Follow-up',
  'Post-Tx',
] as const

/** Maps chart / dropdown labels to persisted `ICampaignResponse.type` (enum values). */
const campaignCategoryToType: Record<
  (typeof campaignCategories)[number],
  CampaignType
> = {
  Promotion: CampaignType.Promotion,
  Recall: CampaignType.Recall,
  Reminder: CampaignType.Reminder,
  Celebration: CampaignType.Celebration,
  'Pat. edu': CampaignType['Pat. edu'],
  'Follow-up': CampaignType['Follow-up'],
  'Post-Tx': CampaignType['Post-Tx'],
}

const useCampaignType = (
  overviewDateFilter: marketingOverviewFilter,
): CampaignTypeHooks => {
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const selectedDate = useAppSelector((state) => state.marketing.selectedDate)

  const [selectedCampaign, setSelectedCampaign] = useState<string>('Select all')
  const [data, setData] = useState<ICampaignType[]>([])

  const campaignDropdownOptions = useMemo(() => {
    return ['Select all', ...campaignCategories]
  }, [])

  const generateXDates = useCallback(
    (filter: marketingOverviewFilter): { type: string; value: Date[] } => {
      const _xValues: Date[] = []
      let type: string = ''
      const today = new Date(selectedDate)
      switch (filter) {
        case marketingOverviewFilter['1D']:
          for (let i = 0; i < 24; i += 4) {
            const _value = new Date(selectedDate)
            _value.setHours(i, 0, 0, 0)
            _xValues.push(_value)
          }
          const _value = new Date(selectedDate)
          _value.setHours(23, 59, 59, 999)
          _xValues.push(_value)
          type = 'time'
          break
        case marketingOverviewFilter['1W']:
          for (let i = 6; i >= -1; i--) {
            const date = new Date(today)
            date.setDate(today.getDate() - i)
            _xValues.push(date)
          }
          type = 'date'
          break
        case marketingOverviewFilter['1M']:
          const oneMonthAgo = new Date(today)
          oneMonthAgo.setMonth(today.getMonth() - 1)
          const interval = 30 / 9 // 9 intervals for 10 points

          for (let i = 0; i < 11; i++) {
            const date = new Date(oneMonthAgo)
            date.setDate(oneMonthAgo.getDate() + Math.round(i * interval))
            _xValues.push(date)
          }
          type = 'date'
          break
        case marketingOverviewFilter['1Y']:
          for (let i = 11; i >= -1; i--) {
            const date = new Date(today)
            date.setMonth(today.getMonth() - i)
            date.setDate(1)
            _xValues.push(date)
          }
          type = 'month'
          break
        case marketingOverviewFilter['YTD']:
          for (let i = 0; i < 13; i++) {
            const date = new Date(today)
            date.setMonth(i)
            date.setDate(1)
            _xValues.push(date)
          }
          type = 'month'
          break
      }
      return { type, value: _xValues }
    },
    [selectedDate],
  )

  const getXValue = (x: Date, type: string): string => {
    switch (type) {
      case 'time':
        return format(x, 'HH:mm')
      case 'date':
        return format(x, 'MMM d')
      case 'month':
        return format(x, 'MMM yyyy')
      default:
        return ''
    }
  }

  const getCampaignTypeCount = (
    _data: ICampaignResponse[],
    type: CampaignType,
    dateRange: { start: Date; end: Date },
  ): number => {
    return _data.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.message_type === 'Outgoing' &&
            campaign.type === type &&
            m.date_time_sent &&
            isDateInRange(
              m.date_time_sent,
              dateRange.start,
              dateRange.end,
              false,
            ),
        ).length
      )
    }, 0)
  }

  const calculateCampaignType = useCallback((): void => {
    let _campaigns: ICampaignResponse[]
    if (selectedCampaign === 'Select all') {
      _campaigns = campaigns
    } else {
      const wantedType =
        campaignCategoryToType[
          selectedCampaign as keyof typeof campaignCategoryToType
        ]
      _campaigns =
        wantedType === undefined
          ? []
          : campaigns.filter((c) => c.type === wantedType)
    }

    const xValues = generateXDates(overviewDateFilter)
    const _data: ICampaignType[] = []
    xValues.value.forEach((x, index) => {
      if (index !== xValues.value.length - 1) {
        const dateRange = { start: x, end: xValues.value[index + 1] }

        _data.push({
          x: getXValue(x, xValues.type),
          Promotion: getCampaignTypeCount(
            _campaigns,
            CampaignType.Promotion,
            dateRange,
          ),
          Recall: getCampaignTypeCount(
            _campaigns,
            CampaignType.Recall,
            dateRange,
          ),
          Reminder: getCampaignTypeCount(
            _campaigns,
            CampaignType.Reminder,
            dateRange,
          ),
          Celebration: getCampaignTypeCount(
            _campaigns,
            CampaignType.Celebration,
            dateRange,
          ),
          'Pat. edu': getCampaignTypeCount(
            _campaigns,
            CampaignType['Pat. edu'],
            dateRange,
          ),
          'Follow-up': getCampaignTypeCount(
            _campaigns,
            CampaignType['Follow-up'],
            dateRange,
          ),
          'Post-Tx': getCampaignTypeCount(
            _campaigns,
            CampaignType['Post-Tx'],
            dateRange,
          ),
        })
        setData(_data)
      }
    })
  }, [overviewDateFilter, selectedCampaign, selectedDate, campaigns])

  useEffect(() => {
    calculateCampaignType()
  }, [overviewDateFilter, selectedCampaign, selectedDate, campaigns])

  return {
    campaigns,
    campaignCategories: [...campaignCategories],
    campaignDropdownOptions,
    data,
    selectedCampaign,
    setSelectedCampaign,
  }
}

export default useCampaignType
